import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { MobileBlocker } from '../../../../components/mobile-blocker';
import { type BulkGroup } from '../../../../entity/api/bulk-group';
import type { Product } from '../../../../entity/api/product';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { captureException } from '../../../../logger/capture-exception';
import { getQueryClient } from '../../../../sdk/request';
import {
  fetchBulkGroupsByBrandIdImperative,
  fetchMediaListImperative,
  fetchProductsByBrandIdImperative,
  useBulkGroupsByBrandId,
  useProductsByBrandId,
  useUsersByIds,
} from '../../../../services/storage';
import { getAuthUserFromCache } from '../../../../services/storage/users-queries';
import { getSelectedBrand } from '../../../../shared/brand';
import { Feature, isFeatureAvailable } from '../../../../shared/feature';
import { BulkProductListing } from '../components/-bulk-product-listing';
import { HeaderProducts } from '../components/-header-products';

export const Route = createFileRoute('/app/_sidebar-layout/products/bulk-import/')({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/products/bulk-import',
      level: 'info',
    });

    // Get user from TanStack Query cache (populated during auth initialization)
    const queryClient = getQueryClient();
    const currentUser = getAuthUserFromCache(queryClient);
    const currentBrand = getSelectedBrand(currentUser);
    if (currentBrand?._id == null) {
      // user shouldn't access this page without a current org,
      // make him refresh his page
      window.location.href = '/app';
      return;
    }

    return Promise.allSettled([
      fetchProductsByBrandIdImperative(queryClient, currentBrand._id),
      fetchBulkGroupsByBrandIdImperative(queryClient, currentBrand._id),
      fetchMediaListImperative(queryClient),
    ]).catch(function (err) {
      captureException(
        err,
        {
          file: 'app/_sidebar-layout.products/bulk-import/index.tsx',
          method: 'beforeLoad',
          infoMessage: 'unable to fetch bulk group and media',
        },
        trackError
      );
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();

  // TanStack Query hook for auth user
  const queryClient = getQueryClient();
  const currentUser = getAuthUserFromCache(queryClient);
  const currentBrand = getSelectedBrand(currentUser);

  // TanStack Query for products and bulk groups
  const { data: { products: productsList } = { products: [] } } = useProductsByBrandId(
    currentBrand?._id
  );
  const { data: bulkGroups = [] } = useBulkGroupsByBrandId(currentBrand?._id);

  const [filteredBulk, setFilteredBulk] = useState<BulkGroup[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [sortByDir, setSortByDir] = useState<'ASC' | 'DESC'>('DESC');

  // Extract unique author IDs from bulk groups
  const bulkAuthorIds = useMemo(
    function () {
      if (bulkGroups.length === 0 || currentUser == null) {
        return [];
      }
      const ids = bulkGroups.map((b) => b.author).filter((id): id is string => id != null);
      return [...new Set(ids)]; // deduplicate
    },
    [bulkGroups, currentUser]
  );

  // TanStack Query hook for fetching authors by IDs
  const { data: authorsData } = useUsersByIds(bulkAuthorIds);
  const authorsList = authorsData ?? [];

  function handleSortBy(sortByValue: string) {
    setSortByDir(sortByValue as 'ASC' | 'DESC');
    setFilteredBulk(function (prev) {
      return [...prev].sort(function (a, b) {
        return sortByValue === 'ASC' ? a.updatedAt - b.updatedAt : b.updatedAt - a.updatedAt;
      });
    });
  }

  useEffect(
    function () {
      if (bulkGroups.length === 0) {
        navigate({ to: '/app/products' }).catch(function (err) {
          captureException(
            err,
            {
              file: 'app/_sidebar-layout.products/bulk-import/index.tsx',
              method: 'useEffect',
              infoMessage: 'unable to navigate to /app/products',
            },
            trackError
          );
        });
        return;
      }

      setFilteredBulk(bulkGroups);
    },
    [bulkGroups, navigate]
  );

  useEffect(
    function () {
      setFilteredProducts(productsList);
    },
    [productsList]
  );

  useEffect(
    function () {
      if (!isFeatureAvailable(Feature.PRODUCT)) {
        navigate({ to: '/app/blogs' }).catch(function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/_sidebar-layout.products/bulk-import/index.tsx',
              method: 'navigate',
              infoMessage: 'unable to navigate to /app/blogs',
            },
            trackError
          );
        });
      }
    },
    [navigate]
  );

  return (
    <>
      <MobileBlocker />
      <div className="h-full w-full flex-1 overflow-y-auto">
        <div className="mx-auto flex h-full w-full max-w-content flex-col items-start">
          <HeaderProducts
            filteredProducts={filteredProducts}
            currentUser={currentUser}
            onSortDirChange={handleSortBy}
            sortByDir={sortByDir}
          />
          <BulkProductListing bulkListing={filteredBulk} authorList={authorsList} />
        </div>
      </div>
    </>
  );
}
